
import React from 'react';
import { Card } from '@/components/ui/card';
import { Calendar, Clock, DollarSign, MapPin, Users } from 'lucide-react';
import type { Tables } from '@/integrations/supabase/types';

interface BookingCardProps {
  booking: Tables<'booking_details'>;
  index: number;
  isUpcoming: boolean;
  formatDate: (dateString: string) => string;
  formatCurrency: (amount: number) => string;
}

export const BookingCard: React.FC<BookingCardProps> = ({ 
  booking, 
  index, 
  isUpcoming, 
  formatDate, 
  formatCurrency 
}) => {
  return (
    <Card 
      key={booking.id || index}
      className="p-4 hover-lift touch-feedback active:scale-98 animate-slide-in-bottom"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center">
              <div className={`h-8 w-8 rounded-full ${isUpcoming ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'} flex items-center justify-center mr-2`}>
                <Calendar className="h-4 w-4" />
              </div>
              <div>
                <h3 className="font-medium">{booking.name || 'Untitled Event'}</h3>
                <div className="flex items-center text-xs text-muted-foreground mt-0.5">
                  <Users className="h-3 w-3 mr-1" />
                  <span>{booking.client_name || 'No client'}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center text-sm font-medium">
            <span className={`${isUpcoming ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'} rounded-full px-2 py-0.5 text-xs`}>
              {formatDate(booking.start_date ?? '')}
            </span>
          </div>
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center text-muted-foreground">
            <Clock className="h-3.5 w-3.5 mr-1.5" />
            <span>{booking.duration_days ? `${booking.duration_days} day${booking.duration_days !== 1 ? 's' : ''}` : 'Time TBD'}</span>
          </div>
          
          <div className="flex items-center text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 mr-1.5" />
            <span>{booking.venue_name || 'No venue'}</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="text-xs text-muted-foreground">
            {booking.total_shifts || 0} {booking.total_shifts === 1 ? 'shift' : 'shifts'}
          </div>
          
          <div className="flex items-center text-sm font-medium">
            <DollarSign className="h-3.5 w-3.5 mr-0.5 text-green-500" />
            <span>{formatCurrency(booking.total_billing ?? 0)}</span>
          </div>
        </div>
      </div>
    </Card>
  );
};
